import React, { useEffect, useState, useMemo } from "react";
import { supabase } from "../supabaseClient";
import "./UserDashboardModal.css";

import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
} from "recharts";

const COLORS = [
  "#6366f1",
  "#8b5cf6",
  "#ec4899",
  "#f97316",
  "#10b981",
  "#06b6d4",
  "#eab308",
  "#ef4444",
];

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="dashboard-tooltip">
      <strong>{payload[0].name}</strong>
      <div>{payload[0].value}</div>
    </div>
  );
};

export default function UserDashboardModal({ show, onClose, user, isDarkMode }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show || !user) return;

    const fetchItems = async () => {
      setLoading(true);

      const userId = user.id || user.user_id || user.auth_user_id;

      const { data, error } = await supabase
        .from("user_items")
        .select(`
          id,
          price_paid,
          estimated_value,
          category:categories!user_items_category_fkey ( name ),
          subcategory:subcategories!user_items_subcategory_id_fkey ( name )
        `)
        .eq("user_id", userId);

      if (error) {
        console.error("Dashboard fetch error:", error);
        setItems([]);
      } else {
        setItems(data || []);
      }

      setLoading(false);
    };

    fetchItems();
  }, [show, user]);

  const totalItems = items.length;

  const categoryData = useMemo(() => {
    return Object.values(
      items.reduce((acc, item) => {
        const name = item.category?.name || "Uncategorized";

        acc[name] = acc[name] || {
          name,
          value: 0,
        };

        acc[name].value += 1;

        return acc;
      }, {})
    );
  }, [items]);

  const subcategoryData = useMemo(() => {
      return Object.values(
        items.reduce((acc, item) => {
          const name = item.subcategory?.name || "Unspecified";

          acc[name] = acc[name] || {
            name,
            count: 0,
          };

          acc[name].count += 1;

          return acc;
        }, {})
      )
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);
    }, [items]);

  const priceStats = useMemo(() => {
    const prices = items
      .map((i) => Number(i.price_paid))
      .filter((p) => !isNaN(p));

    const values = items
      .map((i) => Number(i.estimated_value))
      .filter((p) => !isNaN(p));

    const totalSpent = prices.reduce((a, b) => a + b, 0);
    const estValue = values.reduce((a, b) => a + b, 0);

    return {
      totalSpent,
      estValue,
    };
  }, [items]);

  if (!show) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
          className={`modal-content dashboard-modal ${
            isDarkMode ? "dark" : ""
          }`}
          onClick={(e) => e.stopPropagation()}
        >
        <h2 className="dashboard-title">
          📊 My Collection Dashboard
        </h2>

        {loading ? (
          <p>Loading reports...</p>
        ) : (
          <>
            <div className="dashboard-stats">
              <div className="dashboard-card">
                <div className="dashboard-card-label">
                  Total Items
                </div>
                <div className="dashboard-card-value">
                  {totalItems.toLocaleString()}
                </div>
              </div>

              <div className="dashboard-card">
                <div className="dashboard-card-label">
                  Total Spent
                </div>
                <div className="dashboard-card-value">
                  $
                  {priceStats.totalSpent.toLocaleString("en-US", {
                    maximumFractionDigits: 0,
                  })}
                </div>
              </div>

              <div className="dashboard-card">
                <div className="dashboard-card-label">
                  Estimated Value
                </div>
                <div className="dashboard-card-value">
                  $
                  {priceStats.estValue.toLocaleString("en-US", {
                    maximumFractionDigits: 0,
                  })}
                </div>
              </div>
            </div>

            <div className="dashboard-chart-card">
              <h3>Items by Category</h3>

              {categoryData.length === 0 ? (
                <p>No category data available</p>
              ) : (
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                   <Pie
                      data={categoryData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={70}
                      outerRadius={110}
                      paddingAngle={3}
                      label={({ name, percent }) =>
                        `${name} ${(percent * 100).toFixed(0)}%`
                      }
                    >
                      {categoryData.map((_, index) => (
                        <Cell
                          key={index}
                          fill={COLORS[index % COLORS.length]}
                          stroke="#fff"
                          strokeWidth={2}
                        />
                      ))}
                    </Pie>

                    <Tooltip content={<CustomTooltip />} />

                    <text
                      x="50%"
                      y="50%"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      className="dashboard-donut-center"
                    >
                      {totalItems}
                    </text>
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="dashboard-chart-card">
              <h3>Top 10 Subcategories</h3>

              {subcategoryData.length === 0 ? (
                <p>No subcategory data available</p>
              ) : (
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={subcategoryData}>
                    <XAxis
                      dataKey="name"
                      angle={-20}
                      textAnchor="end"
                      interval={0}
                      height={80}
                      tick={{
                        fontSize: 11,
                        fill: "currentColor",
                      }}
                    />

                    <YAxis
                      tick={{
                        fontSize: 12,
                        fill: "currentColor",
                      }}
                    />

                    <Tooltip content={<CustomTooltip />} />

                    <Bar
                      dataKey="count"
                      radius={[8, 8, 0, 0]}
                    >
                      {subcategoryData.map((_, index) => (
                        <Cell
                          key={index}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </>
        )}

        <button className="dashboard-close-btn" onClick={onClose}>
          Close
        </button>

        <div className="dashboard-disclaimer">
          Estimated collection values are rough estimates based on
          marketplace listings and should be used as a guide only.
        </div>
      </div>
    </div>
  );
}